import { useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { Paper, Popover } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Payment as PaymentIcon, Person as PersonIcon } from '@material-ui/icons';
import Skeleton from '@material-ui/lab/Skeleton';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@material-ui/lab';

import { formatPrice } from '../../../shared/utils/format-price.util';
import OrderStatusChip from '../../../orders/components/Chips/OrderStatusChip.component';
import AdvStatusChip from '../../../components/Admin/Order/Chip/AdvStatusChip';
import { Order } from '../../../orders/types/order.type';
import { OrderStatus } from '../../../orders/types/order-status.type';
import { ADMIN_ORDER_APP_PATHS } from '../../../orders/constants/orders-app-paths.constant';

const getStatusColor = (status: OrderStatus): string => {
  switch (status) {
    case 'canceled':
      return '#f44336';
    case 'paid':
      return '#4caf50';
    case 'refuse':
      return '#ff9800';
    case 'waiting':
      return '#2196f3';
    case 'refund':
      return '#8e44ad';
    case 'partial':
      return '#f1c40f';
    case 'abandoned':
    default:
      return '#f0f0f0';
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),

    '& .MuiTimeline-root': {
      margin: 0,
    },
  },
  timeLine: {
    padding: '0 0 0 0.5rem',
    marginBottom: 0,

    '& .MuiTimelineItem-missingOppositeContent:before': {
      flex: 'unset',
      padding: 0,
    },

    '& .MuiTimelineContent-root': {
      padding: '3px 16px',
      marginBottom: '1rem',
    },

    '& .MuiTimelineItem-root:last-of-type': {
      '& .MuiTimelineContent-root': {
        marginBottom: 0,
      },
    },
  },
  timelineContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',

    '& section > div': {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',

      '& .MuiSvgIcon-root': {
        width: '15px',
        height: '15px',
      },
    },

    '& p': {
      margin: 0,
    },
  },
  orderLink: {
    color: theme.palette.admin.primary.main,
    textDecoration: 'none',

    '&:hover': {
      textDecoration: 'underline',
    },
  },
  dotHoverArea: {
    display: 'inline-flex',
    cursor: 'default',
  },
  popover: {
    pointerEvents: 'none',
  },
  popoverContent: {
    padding: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',

    '& > p': {
      margin: 0,
      fontSize: '0.875rem',

      '& > span': {
        fontWeight: 'bold',
        color: '#424242',
      },
    },
  },
  popoverChips: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
}));

interface Props {
  isLoading: boolean;
  orders: Order[];
}

export default function LastOrdersTimeline({ isLoading, orders }: Props) {
  const classes = useStyles();

  const [popoverState, setPopoverState] = useState<{ anchorEl: HTMLElement; order: Order } | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, order: Order) =>
    setPopoverState({ anchorEl: event.currentTarget, order });

  const handlePopoverClose = () => setPopoverState(null);

  if (isLoading) return <Skeleton variant="rect" height={300} />;

  return (
    <Paper className={classes.root}>
      <Timeline className={classes.timeLine}>
        {orders.map((order, orderIndex) => (
          <TimelineItem key={order.id}>
            <TimelineSeparator>
              <span
                className={classes.dotHoverArea}
                onMouseEnter={(event: React.MouseEvent<HTMLElement>) => handlePopoverOpen(event, order)}
                onMouseLeave={handlePopoverClose}
              >
                <TimelineDot
                  variant="outlined"
                  style={{
                    borderWidth: '2px',
                    borderColor: getStatusColor(order.status),
                  }}
                />
              </span>
              {orderIndex + 1 !== orders.length && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent className={classes.timelineContent}>
              <p>
                <Link className={classes.orderLink} to={ADMIN_ORDER_APP_PATHS.ORDER_DETAILS(order.id)}>
                  {`n° ${order.id.substring(0, 8)}`}
                </Link>
              </p>
              <section>
                <div>
                  <PersonIcon />
                  <p>{`${order.owner.firstName} ${order.owner.lastName}`}</p>
                </div>
                <div>
                  <PaymentIcon />
                  <p>{formatPrice(order.totals.totalTtc)}</p>
                </div>
              </section>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
      <Popover
        className={classes.popover}
        open={Boolean(popoverState)}
        anchorEl={popoverState?.anchorEl}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        {popoverState && (
          <div className={classes.popoverContent}>
            <p>
              <span>Livraison:</span> {moment(popoverState.order.deliveryInfos.deliveryDate).format('DD/MM/YYYY')}
            </p>
            <div className={classes.popoverChips}>
              <OrderStatusChip status={popoverState.order.status} size="small" />
              <AdvStatusChip advStatus={popoverState.order.advStatus} />
            </div>
          </div>
        )}
      </Popover>
    </Paper>
  );
}
